// @ts-expect-error pure js
import { DIAGNOSTIC_TAGS, FileTag } from "./constants/documents";

/** Caractéristiques d'un bien dont dépendent les diagnostics exigibles. */
export interface DiagnosticRuleInput {
  /** `habitation` (défaut), `commercial` ou `garage` (parking, stockage, autre local). */
  type?: string | null;
  /** Année sur 4 chiffres, ou période des anciens formulaires (« avant 1949 », « entre 1949 et 1974 »…). */
  year_built?: string | null;
  surface_total?: number | null;
  /** Zone A à D du plan d'exposition au bruit couvrant l'adresse, null hors zone. */
  noise_exposure_zone?: string | null;
  /** Date de la vérification de la zone de bruit ; absente tant que l'adresse n'a pas été vérifiée. */
  noise_exposure_checked_at?: string | null;
}

export interface DiagnosticRequirement {
  required: boolean;
  /** Règle appliquée, affichée à l'utilisateur ; absente quand le diagnostic est dû sans condition. */
  reason?: string;
}

/** Bornes (incluses) de l'année de construction. */
export interface YearRange {
  min?: number;
  max?: number;
}

const DIAGNOSTIC_TAG_LIST = DIAGNOSTIC_TAGS as string[];

/** Libellés courts des diagnostics (colonnes du pilotage, puces de l'onglet Documents). */
export const DIAGNOSTIC_SHORT_LABELS: Record<string, string> = {
  [FileTag.DPE]: "DPE",
  [FileTag.PLOMB]: "Plomb",
  [FileTag.AMIANTE]: "Amiante",
  [FileTag.ELEC]: "Électricité",
  [FileTag.GAZ]: "Gaz",
  [FileTag.RISQUE_NATUREL]: "ERP",
  [FileTag.BRUIT]: "Bruit",
  [FileTag.ANNEXE_ENVIRONNEMENTALE]: "Annexe env.",
};

/** Âge d'installation (électricité, gaz) au-delà duquel l'état de l'installation est exigé. */
const INSTALLATION_AGE_YEARS = 15;
/** Surface d'un bail commercial (bureaux, commerces) à partir de laquelle l'annexe environnementale est due. */
const ANNEXE_ENVIRONNEMENTALE_SURFACE_M2 = 2000;

export function parseYearBuilt(value?: string | null): YearRange | null {
  const trimmed = value?.trim() ?? "";
  const exact = /^(\d{4})$/.exec(trimmed);
  if (exact) {
    const year = Number(exact[1]);
    return { min: year, max: year };
  }
  const avant = /^avant (\d{4})$/i.exec(trimmed);
  if (avant) {
    return { max: Number(avant[1]) - 1 };
  }
  const apres = /^apr[èe]s (\d{4})$/i.exec(trimmed);
  if (apres) {
    return { min: Number(apres[1]) + 1 };
  }
  const entre = /^entre (\d{4}) et (\d{4})$/i.exec(trimmed);
  if (entre) {
    return { min: Number(entre[1]), max: Number(entre[2]) };
  }
  return null;
}

type Verdict = "yes" | "no" | "unknown";

/** Le bien a-t-il été construit strictement avant `year` ? `unknown` si l'année manque ou si la période chevauche. */
function builtBefore(range: YearRange | null, year: number): Verdict {
  if (!range) {
    return "unknown";
  }
  if (range.max !== undefined && range.max < year) {
    return "yes";
  }
  if (range.min !== undefined && range.min >= year) {
    return "no";
  }
  return "unknown";
}

const REQUIRED: DiagnosticRequirement = { required: true };

function required(reason: string): DiagnosticRequirement {
  return { required: true, reason };
}

function notRequired(reason: string): DiagnosticRequirement {
  return { required: false, reason };
}

function unknownYearReason(range: YearRange | null): string {
  return range
    ? "Période de construction imprécise : diagnostic demandé par défaut"
    : "Année de construction non renseignée : diagnostic demandé par défaut";
}

type PropertyKind = "habitation" | "commercial" | "garage";

function propertyKind(type?: string | null): PropertyKind {
  if (type === "commercial" || type === "garage") {
    return type;
  }
  return "habitation";
}

const NOT_FOR_COMMERCIAL = "Non exigé pour un local commercial";
const NOT_FOR_GARAGE =
  "Non exigé pour un garage, un parking ou un local de stockage";

function installationRequirement(
  range: YearRange | null,
  today: Date,
  installation: string,
): DiagnosticRequirement {
  // L'état est dû pour une installation de plus de 15 ans : l'année de construction sert
  // d'approximation, le bailleur dispense le bien si l'installation a été refaite.
  const threshold = today.getFullYear() - INSTALLATION_AGE_YEARS;
  const before = builtBefore(range, threshold + 1);
  if (before === "no") {
    return notRequired(`${installation} de moins de 15 ans`);
  }
  if (before === "yes") {
    return required(`${installation} de plus de 15 ans`);
  }
  return required(unknownYearReason(range));
}

/**
 * Le bien est-il tenu de fournir ce diagnostic, et pourquoi ? Faute d'information
 * (année de construction, zone de bruit), le diagnostic est demandé.
 */
export function getDiagnosticRequirement(
  property: DiagnosticRuleInput,
  tag: string,
  today: Date = new Date(),
): DiagnosticRequirement {
  const kind = propertyKind(property.type);
  const range = parseYearBuilt(property.year_built);

  switch (tag) {
    case FileTag.DPE:
      return kind === "garage" ? notRequired(NOT_FOR_GARAGE) : REQUIRED;

    case FileTag.RISQUE_NATUREL:
      return REQUIRED;

    case FileTag.PLOMB: {
      if (kind === "garage") {
        return notRequired(NOT_FOR_GARAGE);
      }
      const before = builtBefore(range, 1949);
      if (before === "no") {
        return notRequired(
          "Construit à partir de 1949 : le constat de risque d'exposition au plomb ne concerne que les immeubles antérieurs",
        );
      }
      return required(
        before === "yes" ? "Construit avant 1949" : unknownYearReason(range),
      );
    }

    case FileTag.AMIANTE: {
      // Permis de construire antérieur au 1er juillet 1997 : une construction de 1997 reste ambiguë.
      if (range?.min !== undefined && range.min >= 1998) {
        return notRequired(
          "Permis de construire postérieur au 1er juillet 1997",
        );
      }
      if (range?.max !== undefined && range.max <= 1996) {
        return required("Permis de construire antérieur au 1er juillet 1997");
      }
      return required(unknownYearReason(range));
    }

    case FileTag.ELEC:
      if (kind !== "habitation") {
        return notRequired(
          kind === "commercial" ? NOT_FOR_COMMERCIAL : NOT_FOR_GARAGE,
        );
      }
      return installationRequirement(range, today, "Installation électrique");

    case FileTag.GAZ:
      if (kind !== "habitation") {
        return notRequired(
          kind === "commercial" ? NOT_FOR_COMMERCIAL : NOT_FOR_GARAGE,
        );
      }
      return installationRequirement(
        range,
        today,
        "Installation gaz (à dispenser si le logement n'est pas alimenté au gaz)",
      );

    case FileTag.BRUIT: {
      if (kind !== "habitation") {
        return notRequired(
          kind === "commercial" ? NOT_FOR_COMMERCIAL : NOT_FOR_GARAGE,
        );
      }
      if (property.noise_exposure_zone) {
        return required(
          `Zone ${property.noise_exposure_zone} du plan d'exposition au bruit d'un aérodrome`,
        );
      }
      if (property.noise_exposure_checked_at) {
        return notRequired(
          "Hors zone de bruit d'un plan d'exposition au bruit (PEB)",
        );
      }
      return required(
        "Zone de bruit non vérifiée pour cette adresse : état demandé par défaut",
      );
    }

    case FileTag.ANNEXE_ENVIRONNEMENTALE: {
      if (kind !== "commercial") {
        return notRequired("Ne concerne que les baux commerciaux");
      }
      const surface = property.surface_total;
      if (surface === undefined || surface === null) {
        return notRequired(
          "Surface non renseignée : l'annexe n'est due qu'au-delà de 2 000 m²",
        );
      }
      return surface > ANNEXE_ENVIRONNEMENTALE_SURFACE_M2
        ? required("Local de plus de 2 000 m²")
        : notRequired("Local de 2 000 m² ou moins");
    }

    case FileTag.MESURAGE:
    case FileTag.TERMITE:
      return notRequired("Facultatif en location : exigé à la vente seulement");

    default:
      return REQUIRED;
  }
}

/** Exigences de tous les diagnostics du pilotage pour un bien, indexées par tag. */
export function getDiagnosticRequirements(
  property: DiagnosticRuleInput,
  today: Date = new Date(),
): Record<string, DiagnosticRequirement> {
  return Object.fromEntries(
    DIAGNOSTIC_TAG_LIST.map((tag) => [
      tag,
      getDiagnosticRequirement(property, tag, today),
    ]),
  );
}

/** Tags des diagnostics exigibles pour ce bien, dans l'ordre du pilotage. */
export function getRequiredDiagnosticTags(
  property: DiagnosticRuleInput,
  today: Date = new Date(),
): string[] {
  return DIAGNOSTIC_TAG_LIST.filter(
    (tag) => getDiagnosticRequirement(property, tag, today).required,
  );
}
